import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import { RefreshCw, Save } from 'lucide-react';
import { useNotification } from '../../hooks/useNotification';
import {
  useRefreshUsdVndRate,
  useUpdateUsdVndRate,
  useUsdVndRate,
} from '../../services/exchangeRate.service';
import { FormattedNumberTextField } from '../common/FormattedNumberTextField';

export const ExchangeRateCard: React.FC = () => {
  const notification = useNotification();
  const { data: rate, isLoading, error } = useUsdVndRate();
  const updateMutation = useUpdateUsdVndRate();
  const refreshMutation = useRefreshUsdVndRate();
  const [draft, setDraft] = useState(0);

  useEffect(() => {
    if (rate) setDraft(rate.vndPerUsd);
  }, [rate]);

  const isBusy = updateMutation.isPending || refreshMutation.isPending;
  const hasChanges = !!rate && draft > 0 && draft !== rate.vndPerUsd;

  const run = async (action: () => Promise<unknown>, success: string) => {
    try {
      await action();
      notification.success(success);
    } catch (err) {
      notification.error(err instanceof Error ? err.message : 'Failed to update exchange rate');
    }
  };

  return (
    <Paper
      elevation={0}
      className="mt-6 rounded-xl bg-white p-6 dark:bg-slate-800"
      sx={{ boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)' }}
    >
      <Box className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <Box>
          <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
            Exchange rate (USD → VND)
          </Typography>
          <Typography variant="body2" className="mt-1 text-gray-600 dark:text-slate-400">
            Used to auto-fill package and token pack prices. Fetched from the provider once a day; a
            manual value stays until you switch back to automatic.
          </Typography>
        </Box>
        {rate && (
          <Chip
            size="small"
            color={rate.source === 'manual' ? 'warning' : 'success'}
            label={rate.source === 'manual' ? 'Manual' : 'Automatic'}
          />
        )}
      </Box>

      {isLoading ? (
        <Box className="flex items-center justify-center py-8">
          <CircularProgress size={24} className="text-blue-500" />
        </Box>
      ) : error || !rate ? (
        <Alert severity="error" className="mt-4">
          Exchange rate is unavailable. Try refreshing from the provider.
        </Alert>
      ) : (
        <Box className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-start">
          <FormattedNumberTextField
            size="small"
            label="1 USD = (VND)"
            value={draft}
            onValueChange={(value) => setDraft(value ?? 0)}
            integer={false}
            disabled={isBusy}
            helperText={`Last updated ${new Date(rate.fetchedAt).toLocaleString('en-US')}`}
            sx={{ minWidth: 240 }}
          />
          <Button
            variant="contained"
            startIcon={<Save size={18} />}
            disabled={!hasChanges || isBusy}
            onClick={() =>
              run(() => updateMutation.mutateAsync(draft), 'Exchange rate saved (manual)')
            }
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshCw size={18} />}
            disabled={isBusy}
            onClick={() =>
              run(() => refreshMutation.mutateAsync(), 'Exchange rate refreshed from provider')
            }
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Refresh from provider
          </Button>
        </Box>
      )}
    </Paper>
  );
};
